import { useEffect, useRef, useState } from "react";
import { scoreAttempt } from "@catchup/shared";
import type { SessionAttempt } from "@catchup/shared";
import { useStore } from "../store.ts";
import { collectOnsets, playSlice, type DetectedOnset } from "../engine/audio.ts";
import { expectedOnsets, fadedSchedule, roundWords, toTrackTime } from "../engine/round.ts";
import { logAttempt } from "../api.ts";

type Phase = "ready" | "leadin" | "singing" | "scoring";

/**
 * The round itself.
 *
 * Timing rules that the whole measurement rests on:
 *   - playback start is scheduled on the audio clock and its exact context
 *     time is captured, so we know precisely when the lead-in was heard;
 *   - the handover moment is computed from that start time plus the lead-in
 *     duration, never from a timer callback firing;
 *   - the UI countdown uses rAF purely for painting, and its value is derived
 *     from `ctx.currentTime` — rAF never decides when anything happens.
 */
export function Play() {
  const store = useStore();
  const { chart, round, engine, calibration, config, buffer } = store;

  const [phase, setPhase] = useState<Phase>("ready");
  const [clock, setClock] = useState(0);
  const [level, setLevel] = useState(-90);
  const [onsetCount, setOnsetCount] = useState(0);

  const collector = useRef<ReturnType<typeof collectOnsets> | null>(null);
  const playback = useRef<ReturnType<typeof playSlice> | null>(null);
  const rafId = useRef<number>(0);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafId.current);
      for (const t of timers.current) clearTimeout(t);
      playback.current?.stop();
      collector.current?.stop();
    };
  }, []);

  if (!chart || !round || !engine || !calibration || !buffer) return null;

  const spec = round.spec;
  const leadInDuration = spec.singStart - spec.leadInStart;
  const singDuration = spec.singEnd - spec.singStart;

  function start() {
    const ctx = engine!.ctx;
    setPhase("leadin");
    setOnsetCount(0);

    const coll = collectOnsets(engine!.onset, setLevel);
    collector.current = coll;

    const handle = playSlice(ctx, buffer!, spec.leadInStart, spec.singStart);
    playback.current = handle;

    // The single most important line in the file: the context time at which
    // the player takes over. Everything they sing is measured against this.
    const singStartContextTime = handle.startedAt + leadInDuration;

    // Arm the onset detector a little BEFORE the handover. A player who comes
    // in early is a real and interesting case ("you came in 120ms early") and
    // arming exactly at the boundary would silently discard those onsets.
    const armAt = singStartContextTime - 0.75;
    const armDelayMs = Math.max(0, (armAt - ctx.currentTime) * 1000);

    timers.current.push(
      window.setTimeout(() => {
        void coll.arm();
        setPhase("singing");
      }, armDelayMs),
    );

    timers.current.push(
      window.setTimeout(
        () => finish(singStartContextTime, coll),
        Math.max(0, (singStartContextTime + singDuration + 0.6 - ctx.currentTime) * 1000),
      ),
    );

    const tick = () => {
      const now = engine!.ctx.currentTime;
      setClock(now - handle.startedAt);
      setOnsetCount(coll.onsets.length);
      rafId.current = requestAnimationFrame(tick);
    };
    rafId.current = requestAnimationFrame(tick);
  }

  async function finish(
    singStartContextTime: number,
    coll: ReturnType<typeof collectOnsets>,
  ) {
    cancelAnimationFrame(rafId.current);
    setPhase("scoring");
    const detected: DetectedOnset[] = coll.stop();
    collector.current = null;

    const expected = expectedOnsets(chart!, spec);
    const words = roundWords(chart!, spec);

    const detectedTrack = toTrackTime(
      detected.map((d) => d.time),
      singStartContextTime,
      spec.singStart,
      calibration!.latency,
    );

    const result = scoreAttempt(
      { expected, detected: detectedTrack, tier: spec.tier },
      config,
    );

    const attempt: SessionAttempt = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      round: spec,
      chartMeta: {
        artist: chart!.meta.artist,
        title: chart!.meta.title,
        duration: chart!.meta.duration,
      },
      expectedOnsets: expected,
      expectedWords: words.map((w) => w.text),
      // Raw, uncorrected, in context time — so scoring AND latency correction
      // can both be redone offline if either turns out to be wrong.
      detectedOnsetsRaw: detected.map((d) => d.time),
      singStartContextTime,
      calibration: calibration!,
      detectedStrengths: detected.map((d) => d.strength),
      synthetic: null,
      scoreAtCapture: result.score,
      configAtCapture: JSON.stringify(config),
    };

    useStore.getState().set({ result, screen: "results", lastAttempt: attempt });

    try {
      await logAttempt(attempt);
    } catch {
      // Logging is best-effort; never lose a round because the log write failed.
      // The score is already on screen.
    }
  }

  const sinceSing = clock - leadInDuration;
  const faded = fadedSchedule(chart, spec, config);
  const trackNow = spec.leadInStart + clock;

  return (
    <div className="panel">
      <h2>
        {chart.meta.artist} — {chart.meta.title}
      </h2>
      <p className="sub">
        {round.sectionLabel} · {spec.tier} · &times;{config.tierMultiplier[spec.tier].toFixed(1)}
      </p>

      <div className="stage">
        {phase === "ready" && (
          <>
            <div className="state-label">Ready</div>
            <div className="big">{leadInDuration.toFixed(1)}s lead-in</div>
            <p className="sub" style={{ maxWidth: 420 }}>
              The music plays, then stops dead. Keep singing exactly where it left
              off — you're scored on whether you hold the clock, not on pitch.
            </p>
            <button className="primary" onClick={start}>
              Play lead-in
            </button>
          </>
        )}

        {phase === "leadin" && (
          <>
            <div className="state-label">Listen</div>
            <div className="huge">{Math.max(0, leadInDuration - clock).toFixed(1)}</div>
            <div className="bar" style={{ width: 260 }}>
              <div style={{ width: `${Math.min(100, (clock / leadInDuration) * 100)}%` }} />
            </div>
          </>
        )}

        {phase === "singing" && (
          <>
            <div className="state-label" style={{ color: "var(--accent)" }}>
              Sing
            </div>
            <div className="huge">
              {Math.max(0, singDuration - Math.max(0, sinceSing)).toFixed(1)}
            </div>
            <div className="meter" style={{ maxWidth: 260 }}>
              <div style={{ width: `${Math.max(0, Math.min(100, (level + 60) * 1.8))}%` }} />
            </div>
            <div className="sub">{onsetCount} onsets detected</div>
          </>
        )}

        {phase === "scoring" && <div className="state-label">Scoring…</div>}

        {(phase === "leadin" || phase === "singing") && spec.tier !== "blind" && (
          <div className="lyrics">
            {faded.map((l, i) => (
              <div
                key={i}
                className={`ln ${spec.tier === "faded" && trackNow >= l.hideAt ? "gone" : ""}`}
              >
                {l.text}
              </div>
            ))}
          </div>
        )}
      </div>

      {phase === "ready" && (
        <button onClick={() => useStore.getState().set({ screen: "pick-round" })}>
          Pick a different section
        </button>
      )}
    </div>
  );
}
